import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, UserIcon } from '@phosphor-icons/react';
import { useAuth, useToaster } from '../../../shared/ui';
import {
  apiService,
  UpdateUserData,
  UserProperty,
  CreatePropertyData,
  UpdatePropertyData,
} from '../../../shared/utils';
import { Breadcrumb } from '../components/Breadcrumb';
import { formatDate, getInitials } from '../utils/format';
import { PropertyAddressSection } from '../components/PropertyAddressSection/PropertyAddressSection';
import { ContactDetailsSection } from '../components/ContactDetailsSection/ContactDetailsSection';
import { AddressPromptModal } from '../components/AddressPromptModal/AddressPromptModal';
import './ProfilePage.css';

export const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const toaster = useToaster();

  // Account info edit state
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdateUserData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  // Properties state
  const [properties, setProperties] = useState<UserProperty[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  // Address prompt modal (once per session)
  const [showAddressPrompt, setShowAddressPrompt] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);

  // Load properties on mount
  useEffect(() => {
    if (!user) return;
    const fetchProperties = async () => {
      setPropertiesLoading(true);
      try {
        const res = await apiService.getProperties();
        if (res.success && res.data) {
          setProperties(res.data);
          // Show address prompt once per session if no properties
          if (
            res.data.length === 0 &&
            !sessionStorage.getItem('addressPromptShown')
          ) {
            sessionStorage.setItem('addressPromptShown', '1');
            setShowAddressPrompt(true);
          }
        }
      } catch {
        // silently skip — non-critical
      } finally {
        setPropertiesLoading(false);
      }
    };
    fetchProperties();
  }, [user]);

  useEffect(() => {
    if (!isEditing) {
      setFormData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
      });
    }
  }, [isEditing, user?.firstName, user?.lastName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await apiService.updateUser(user.id, formData);
      if (response.success) {
        toaster.success('Profile updated successfully');
        await refreshUser();
        setIsEditing(false);
      } else {
        toaster.error(response.error || 'Failed to update profile');
      }
    } catch {
      toaster.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    });
    setIsEditing(false);
  };

  // Contact details save
  const handleContactSave = async (data: Pick<UpdateUserData, 'phone'>) => {
    if (!user?.id) return;
    setContactLoading(true);
    try {
      const res = await apiService.updateUser(user.id, data);
      if (res.success) {
        toaster.success('Contact details updated');
        await refreshUser();
      } else {
        toaster.error(res.error || 'Failed to update contact details');
      }
    } catch {
      toaster.error('Failed to update contact details');
    } finally {
      setContactLoading(false);
    }
  };

  // Property CRUD handlers
  const handleAddProperty = async (data: CreatePropertyData) => {
    setPropertiesLoading(true);
    try {
      const res = await apiService.createProperty(data);
      if (res.success && res.data) {
        setProperties(prev => {
          // If new one is primary, clear primary on others
          const updated = data.isPrimary
            ? prev.map(p => ({ ...p, isPrimary: false }))
            : prev;
          return [...updated, res.data!];
        });
        toaster.success('Property added');
      } else {
        toaster.error(res.error || 'Failed to add property');
      }
    } catch {
      toaster.error('Failed to add property');
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleUpdateProperty = async (id: string, data: UpdatePropertyData) => {
    setPropertiesLoading(true);
    try {
      const res = await apiService.updateProperty(id, data);
      if (res.success && res.data) {
        setProperties(prev =>
          prev.map(p => {
            if (data.isPrimary)
              return p.id === id ? res.data! : { ...p, isPrimary: false };
            return p.id === id ? res.data! : p;
          })
        );
        toaster.success('Property updated');
      } else {
        toaster.error(res.error || 'Failed to update property');
      }
    } catch {
      toaster.error('Failed to update property');
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    setPropertiesLoading(true);
    try {
      const res = await apiService.deleteProperty(id);
      if (res.success) {
        setProperties(prev => prev.filter(p => p.id !== id));
        toaster.success('Property removed');
      } else {
        toaster.error(res.error || 'Failed to remove property');
      }
    } catch {
      toaster.error('Failed to remove property');
    } finally {
      setPropertiesLoading(false);
    }
  };

  const handleSetPrimary = async (id: string) => {
    await handleUpdateProperty(id, { isPrimary: true });
  };

  // Address prompt modal save
  const handlePromptSave = async (data: CreatePropertyData) => {
    setPromptLoading(true);
    try {
      const res = await apiService.createProperty(data);
      if (res.success && res.data) {
        setProperties([res.data]);
        toaster.success('Property address saved');
        setShowAddressPrompt(false);
      } else {
        toaster.error(res.error || 'Failed to save address');
      }
    } catch {
      toaster.error('Failed to save address');
    } finally {
      setPromptLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  const statusTag = (
    <span
      className={`mc-tag ${user.isActive ? 'mc-tag--success' : 'mc-tag--neutral'}`}
    >
      {user.isActive && <CheckCircleIcon size={14} weight="fill" />}
      {user.isActive ? t('profilePage.active') : t('profilePage.inactive')}
    </span>
  );

  return (
    <>
      <Breadcrumb items={[{ label: t('app.nav.profile') }]} />

      <div className="mc-heading">
        <div>
          <h1>{t('app.nav.profile')}</h1>
          <p>{t('profilePage.subtitle')}</p>
        </div>
      </div>

      <section className="mc-card">
        <div className="mc-row">
          <h2>{t('profilePage.overview')}</h2>
          {!isEditing && (
            <button
              type="button"
              className="mc-btn mc-btn--outline"
              onClick={() => setIsEditing(true)}
            >
              {t('profilePage.editProfile')}
            </button>
          )}
        </div>
        <div className="mc-profiletop">
          <div className="mc-bigavatar">{getInitials(user)}</div>
          <div>
            <h2 className="profile-page__name">
              {user.firstName} {user.lastName}
            </h2>
            <div className="mc-muted">{user.email}</div>
            {statusTag}
          </div>
        </div>
      </section>

      <section className="mc-card">
        <h2 className="profile-page__section-title">
          <UserIcon size={20} weight="bold" />
          {t('profilePage.accountInformation')}
        </h2>

        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <div className="mc-grid mc-cols2 profile-page__form-row">
              <label className="mc-field">
                {t('profilePage.firstName')} *
                <input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label className="mc-field">
                {t('profilePage.lastName')} *
                <input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </label>
            </div>
            <div className="mc-actions">
              <button
                type="button"
                className="mc-btn mc-btn--outline"
                onClick={handleCancel}
                disabled={loading}
              >
                {t('profilePage.cancel')}
              </button>
              <button
                type="submit"
                className="mc-btn mc-btn--primary"
                disabled={loading}
              >
                {loading
                  ? t('profilePage.saving')
                  : t('profilePage.saveChanges')}
              </button>
            </div>
          </form>
        ) : (
          <div className="mc-data">
            <div>
              <span className="mc-data__label">
                {t('profilePage.firstName')}
              </span>
              <strong>{user.firstName}</strong>
            </div>
            <div>
              <span className="mc-data__label">
                {t('profilePage.lastName')}
              </span>
              <strong>{user.lastName}</strong>
            </div>
            <div>
              <span className="mc-data__label">
                {t('profilePage.memberSince')}
              </span>
              <strong>{formatDate(user.createdAt, i18n.language)}</strong>
            </div>
            <div>
              <span className="mc-data__label">{t('profilePage.email')}</span>
              <strong>{user.email}</strong>
            </div>
            <div>
              <span className="mc-data__label">{t('profilePage.status')}</span>
              {statusTag}
            </div>
          </div>
        )}
      </section>

      <section className="mc-card">
        <ContactDetailsSection
          phone={user.phone}
          email={user.email}
          onSave={handleContactSave}
          loading={contactLoading}
        />
      </section>

      <section className="mc-card">
        <PropertyAddressSection
          properties={properties}
          onAdd={handleAddProperty}
          onUpdate={handleUpdateProperty}
          onDelete={handleDeleteProperty}
          onSetPrimary={handleSetPrimary}
          loading={propertiesLoading}
        />
      </section>

      <AddressPromptModal
        isOpen={showAddressPrompt}
        onClose={() => setShowAddressPrompt(false)}
        onSave={handlePromptSave}
        loading={promptLoading}
      />
    </>
  );
};
